import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UserRole, userRoleFromName } from '../models/role.model';
import { SessionLifecycleService } from './session-lifecycle.service';
import { SessionStaffService } from './session-staff.service';
import { StaffTypeService } from './staff-type.service';

/**
 * The logged-in user's staff type, as the role the trip rules are written against.
 *
 * Resolved in two hops — the user's staff record, then the staff type that record points at.
 *
 * This briefly resolved from the roles catalog instead (`roleId` → `/v1/roles/{id}`), which
 * is the taxonomy permissions ought to live in. It could not work yet: the Add Gestor flow
 * stamps every gestor with the same `STAFF_ROLE_ID` whatever staff type was chosen, so
 * `/v1/auth/me` answers `Staff` for a coordinator, a supervisor and an administrator alike
 * and every one of them resolved to 'unknown'. The staff type is the only field that
 * actually distinguishes them today. Move back to roles once gestor creation assigns a real
 * one — nothing downstream cares which of the two this method reads, only what it returns.
 *
 * Resolved once per session and shared, since every trip screen asks for it.
 *
 * Every failure along the way resolves to 'unknown', which the rules treat as the most
 * restricted role. A user whose staff type cannot be read sees a read-only trip rather than
 * an editable one.
 */
@Injectable({
  providedIn: 'root',
})
export class TripPermissionsService {
  private readonly sessionStaff = inject(SessionStaffService);
  private readonly staffTypeService = inject(StaffTypeService);
  private readonly lifecycle = inject(SessionLifecycleService);

  private readonly roleSignal = signal<UserRole>('unknown');

  /**
   * The resolved role, for templates to gate on.
   *
   * Starts at 'unknown' and settles once `resolveRole()` completes, so a screen reading it
   * before then shows its controls locked rather than briefly editable.
   */
  public readonly role = this.roleSignal.asReadonly();

  private readonly resolvedSignal = signal(false);

  /** Whether the lookup has finished, however it turned out. */
  public readonly resolved = this.resolvedSignal.asReadonly();

  /**
   * Whether the session has no usable role — the lookup finished and produced nothing this
   * app has rules for, because it failed or because the staff type is one the aliases do not
   * cover.
   *
   * Separate from `role() === 'unknown'` on purpose: the role starts at 'unknown' before the
   * lookup completes, so testing the role alone would flash a "we could not read your role"
   * notice at every user on every page load. This stays false until there is an answer.
   */
  public readonly roleUnavailable = computed(
    () => this.resolvedSignal() && this.roleSignal() === 'unknown');

  private resolution: Observable<UserRole> | null = null;

  constructor() {
    // The role belongs to whoever was logged in. Back to the unresolved starting state, so
    // the next account reads its own instead of inheriting this one's permissions.
    this.lifecycle.ended$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.resolution = null;
      this.roleSignal.set('unknown');
      this.resolvedSignal.set(false);
    });
  }

  /** Resolves the role, reusing the in-flight or already-settled lookup. */
  public resolveRole(): Observable<UserRole> {
    if (!this.resolution) {
      this.resolution = this.lookupRole().pipe(
        // Ahead of the tap on purpose: a lookup that answers after the session ended must
        // not write the previous user's role back over the reset.
        takeUntil(this.lifecycle.ended$),
        tap(role => {
          this.roleSignal.set(role);
          this.resolvedSignal.set(true);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.resolution;
  }

  private lookupRole(): Observable<UserRole> {
    return this.sessionStaff.resolveStaff().pipe(
      map(staff => {
        if (!staff) {
          // SessionStaffService has already said why.
          return '';
        }
        const staffTypeId = staff.staffType?.id ?? staff.staffTypeId ?? '';
        if (!staffTypeId) {
          console.warn(
            '[permissions] The staff record carries no staff type —' +
            ' treating as the most restricted role.',
          );
        }
        return staffTypeId;
      }),
      switchMap(staffTypeId => (staffTypeId ? this.loadTypeName(staffTypeId) : of(''))),
      map(name => this.toRole(name)),
      catchError((error: unknown) => {
        console.error('Unable to resolve the staff role', error);
        return of('unknown' as UserRole);
      }),
    );
  }

  /**
   * The staff type's name, read by id.
   *
   * The staff row already nests a `staffType.name` on the endpoints that send it, but it is
   * optional and undocumented there; the type catalog is the authoritative source, so the id
   * is always followed rather than trusting whatever the row happened to carry.
   */
  private loadTypeName(staffTypeId: string): Observable<string> {
    return this.staffTypeService.getById(staffTypeId).pipe(
      map(response => response?.data?.name ?? ''),
      catchError((error: unknown) => {
        console.error('Unable to load the staff type', error);
        return of('');
      }),
    );
  }

  private toRole(name: string): UserRole {
    const role = userRoleFromName(name);
    if (role === 'unknown' && name) {
      // The lookup worked and the name simply isn't one this app has rules for. Logged with
      // the name itself, because that is the one thing needed to fix it: the aliases in
      // role.model live or die by what the workspace actually calls its staff types.
      console.warn(
        `[permissions] Staff type "${name}" matches no known role —` +
        ' treating as the most restricted one. Add it to ROLE_ALIASES in role.model.ts.',
      );
    }
    return role;
  }
}
